import React from 'react';
import { Select, SelectItem, Avatar } from '@heroui/react';
import { UserNoAvatarIcon } from './icons';

interface AccountMultiSelectProps<TAccount> {
  accounts: TAccount[];
  selectedIds: string[];
  onSelectionChange: (accountIds: string[]) => void;
  getId: (account: TAccount) => string;
  getAvatar?: (account: TAccount) => string | undefined | null;
  getLabel?: (account: TAccount) => string;
  getDescription?: (account: TAccount) => string | undefined;
  placeholder?: string;
  className?: string;
  selectClassNames?: Partial<React.ComponentProps<typeof Select>['classNames']>;
  isLoading?: boolean;
  isRequired?: boolean;
  onClose?: () => void;
}

const AccountMultiSelect = <TAccount,>({
  accounts,
  selectedIds,
  onSelectionChange,
  getId,
  getAvatar,
  getLabel,
  getDescription,
  placeholder = 'Select accounts',
  className = '',
  selectClassNames = {},
  isLoading = false,
  isRequired = false,
  onClose,
}: AccountMultiSelectProps<TAccount>) => {
  const selectedAccounts = selectedIds
    .map((id) => accounts.find((account) => String(getId(account)) === String(id)))
    .filter((account): account is TAccount => Boolean(account));

  const handleSelectionChange = (keys: any) => {
    if (typeof keys === 'string') {
      onSelectionChange([String(keys)]);
      return;
    }

    if (keys === 'all') {
      const allAccountIds = accounts.map((account) => String(getId(account)));
      onSelectionChange(allAccountIds);
      return;
    }

    const values = Array.from(keys as Set<React.Key>).map(String);
    onSelectionChange(values);
  };

  const classNames = {
    trigger: `min-h-[2.375rem] !rounded-2 ${selectedIds.length > 0 ? 'py-1' : ''} ${
      selectClassNames?.trigger ?? ''
    }`.trim(),
    value: `text-sm ${selectClassNames?.value ?? ''}`.trim(),
    popoverContent: `!rounded-3 ${selectClassNames?.popoverContent ?? ''}`.trim(),
    listbox: `p-1.5 ${selectClassNames?.listbox ?? ''}`.trim(),
    label: selectClassNames?.label,
  };

  return (
    <Select
      aria-label="Account selection"
      placeholder={placeholder}
      selectedKeys={selectedIds}
      selectionMode="multiple"
      onSelectionChange={handleSelectionChange}
      isLoading={isLoading}
      isRequired={isRequired}
      onClose={onClose}
      variant="bordered"
      className={`!rounded-2 border-auth-strength-none ${className}`}
      classNames={classNames}
      renderValue={() => {
        if (selectedAccounts.length === 0) {
          return placeholder;
        }

        return (
          <div className="flex flex-wrap gap-2">
            {selectedAccounts.map((account) => {
              const id = String(getId(account));
              const avatar = getAvatar?.(account) || null;
              const label =
                getLabel?.(account) ||
                `@${(account as any)?.of_user_name || (account as any)?.username || id}`;

              return (
                <div
                  key={id}
                  className="flex items-center gap-1.5 bg-auth-surface rounded-1.5 px-1.5 py-1"
                >
                  <Avatar
                    src={avatar || undefined}
                    fallback={<UserNoAvatarIcon />}
                    className="w-5 h-5 rounded-1.5 flex-shrink-0"
                  />
                  <span className="text-sm text-auth-ink">{label}</span>
                  <button
                    type="button"
                    className="relative shrink-0 size-3.5 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      const newSelection = selectedIds.filter(
                        (selectedId) => String(selectedId) !== String(id)
                      );
                      onSelectionChange(newSelection);
                    }}
                  >
                    <svg className="w-3 h-3 text-neutral-8" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        );
      }}
    >
      {accounts.map((account) => {
        const id = String(getId(account));
        const avatar = getAvatar?.(account) || null;
        const label =
          getLabel?.(account) ||
          `@${(account as any)?.of_user_name || (account as any)?.username || id}`;
        const description = getDescription?.(account);

        return (
          <SelectItem
            key={id}
            textValue={label}
            classNames={{
              base: 'p-2 hover:bg-auth-surface rounded-lg',
            }}
          >
            <div className="flex items-center gap-2">
              <Avatar
                src={avatar || undefined}
                fallback={<UserNoAvatarIcon />}
                className="w-6 h-6 rounded-lg flex-shrink-0"
              />
              <span className="text-sm">{label}</span>
              {description ? (
                <span className="text-xs text-auth-muted capitalize">{description}</span>
              ) : null}
            </div>
          </SelectItem>
        );
      })}
    </Select>
  );
};

export default AccountMultiSelect;
